import { Permission } from '@up2stay/up2promo-api-client';
import { Button, Card, CardContent, Skeleton } from '@up2stay/up2promo-ui';
import { CalendarPlus } from 'lucide-react';
import { useState } from 'react';

import { AppIonPage } from '@/core/components/app-ion-page';
import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { LeadsEventsHub } from '@/core/components/leads/leads-events-hub';
import { PageHeader } from '@/core/components/page-header';
import { useIsMobile } from '@/core/hooks/use-mobile';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useProfile } from '@/core/hooks/use-profile';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

export function LeadsEventsPage() {
  const dict = useDictionary();
  const eventsDict = dict.leads.events;
  const isMobile = useIsMobile();
  const { can } = usePermissions();
  const canManageEvents = can(Permission.EVENTS_MANAGE);
  const { isLoading: profileLoading } = useProfile();
  const currentWorkspace = useCurrentWorkspace();
  const [createEventOpen, setCreateEventOpen] = useState(false);

  const headerActions =
    canManageEvents && currentWorkspace?.id ? (
      <Button
        type="button"
        variant="primary"
        className={
          isMobile
            ? 'size-11 rounded-full shadow-[var(--shadow-brand-cta)]'
            : 'h-9 gap-1.5 rounded-[var(--radius-input)] px-3.5 text-[13px] font-semibold shadow-[var(--shadow-brand-cta)]'
        }
        aria-label={eventsDict.create}
        onClick={() => setCreateEventOpen(true)}
      >
        <CalendarPlus className={isMobile ? 'size-[18px]' : 'size-[15px]'} />
        {isMobile ? null : eventsDict.create}
      </Button>
    ) : null;

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout className="min-w-0 overflow-x-hidden">
          <div data-guidance-target="leads-inbox">
            <PageHeader
              title={eventsDict.title}
              description={!isMobile ? eventsDict.description : undefined}
              breadcrumb={
                isMobile ? undefined : (
                  <>
                    {dict.nav.leads}
                    <span className="mx-1.5 text-muted-foreground/70">/</span>
                    {eventsDict.title}
                  </>
                )
              }
              actions={headerActions}
              actionsLayout="inline"
            />
          </div>

          {profileLoading ? (
            <Card>
              <CardContent className="flex flex-col gap-4 p-5 sm:p-6">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-64 w-full" />
              </CardContent>
            </Card>
          ) : currentWorkspace?.id ? (
            <LeadsEventsHub
              workspaceId={currentWorkspace.id}
              hideIntro
              createOpen={createEventOpen}
              onCreateOpenChange={setCreateEventOpen}
            />
          ) : null}
        </AppPageLayout>
      </AppPageContent>
    </AppIonPage>
  );
}
